import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, Package, Layers, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PackagingProgress({ orderId, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderId) return;
    let cancelled = false;
    const load = () => {
      axios.get(`${API}/orders/${orderId}/packaging-progress`)
        .then(r => { if (!cancelled) { setData(r.data); setLoading(false); } })
        .catch(() => { if (!cancelled) setLoading(false); });
    };
    load();
    const iv = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [orderId]);

  if (loading) return <div className="p-2 text-center"><Loader2 className="h-4 w-4 animate-spin mx-auto" /></div>;
  if (!data) return null;

  const boxPct = data.boxes_needed ? Math.min(100, (data.boxes_done / data.boxes_needed) * 100) : 0;
  const palPct = data.pallets_needed ? Math.min(100, (data.pallets_done / data.pallets_needed) * 100) : 0;

  if (compact) {
    return (
      <div className="grid grid-cols-3 gap-2 text-xs" data-testid="packaging-progress-compact">
        <div className="bg-emerald-50 border border-emerald-200 rounded p-2 text-center">
          <div className="text-[9px] uppercase text-emerald-700 font-bold flex items-center justify-center gap-1"><Package className="h-3 w-3" />Caixas</div>
          <div className="text-lg font-black text-emerald-800 mt-1">{data.boxes_done}<span className="text-slate-400 text-sm">/{data.boxes_needed}</span></div>
          <div className="text-[9px] text-slate-500">faltam {data.boxes_remaining}</div>
        </div>
        <div className="bg-indigo-50 border border-indigo-200 rounded p-2 text-center">
          <div className="text-[9px] uppercase text-indigo-700 font-bold flex items-center justify-center gap-1"><Layers className="h-3 w-3" />Paletes</div>
          <div className="text-lg font-black text-indigo-800 mt-1">{data.pallets_done}<span className="text-slate-400 text-sm">/{data.pallets_needed || "?"}</span></div>
          <div className="text-[9px] text-slate-500">faltam {data.pallets_remaining}</div>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded p-2 text-center">
          <div className="text-[9px] uppercase text-amber-700 font-bold flex items-center justify-center gap-1"><QrCode className="h-3 w-3" />Etiquetas</div>
          <div className="text-lg font-black text-amber-800 mt-1">{data.labels_generated}</div>
          <div className="text-[9px] text-slate-500">geradas</div>
        </div>
      </div>
    );
  }

  return (
    <div className="border rounded-lg bg-white p-4 space-y-3" data-testid="packaging-progress">
      <div className="text-sm font-bold flex items-center gap-2"><Package className="h-4 w-4 text-emerald-600" />Progresso de Embalagem</div>
      <div className="text-[10px] text-slate-500">
        {data.units_per_bag} u/saco · {data.bags_per_box} sacos/caixa ({data.units_per_box} u/caixa) · {data.boxes_per_pallet || "?"} caixas/palete
      </div>

      {/* Caixas */}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="font-semibold">Caixas</span>
          <span className="font-mono"><strong className="text-emerald-700">{data.boxes_done}</strong> / {data.boxes_needed} (faltam {data.boxes_remaining})</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full" style={{ width: `${boxPct}%` }}></div>
        </div>
      </div>

      {/* Paletes */}
      <div>
        <div className="flex justify-between text-xs mb-1">
          <span className="font-semibold">Paletes</span>
          <span className="font-mono"><strong className="text-indigo-700">{data.pallets_done}</strong> / {data.pallets_needed || "—"} (faltam {data.pallets_remaining})</span>
        </div>
        <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-indigo-500 to-indigo-600 rounded-full" style={{ width: `${palPct}%` }}></div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-center pt-2 border-t">
        <div><div className="text-[10px] uppercase text-slate-500">Peças boas</div><div className="font-black text-emerald-700">{data.good_quantity.toLocaleString()}</div></div>
        <div><div className="text-[10px] uppercase text-slate-500">Objectivo</div><div className="font-black">{data.target.toLocaleString()}</div></div>
        <div><div className="text-[10px] uppercase text-slate-500">Etiquetas</div><div className="font-black text-amber-700">{data.labels_generated}</div></div>
      </div>
    </div>
  );
}
